import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import UserGroupChip from './UserGroupChip';
import UserGroupSwitcherMenu from './UserGroupSwitcherMenu';

// Wrapper around the chip + menu. Owns:
//   - The anchorEl state that drives the dropdown.
//   - The reload-after-switch network calls (matches the legacy switcher's
//     pattern; full-page reload picks up the new JWT cleanly).
//   - The combined chip+avatar visual cluster — the avatar (rendered by the
//     caller, passed via `avatarSlot`) sits on top of the chip's right edge.
const UserGroupChipCluster = ({
  user,
  faviconUrl,
  navbarColor,
  accentColor,
  avatarSlot
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const groups = useMemo(
    () => (Array.isArray(user?.groups) ? user.groups : []),
    [user]
  );
  const isMultiGroup = groups.length > 1;

  const currentGroupId = useMemo(() => {
    const raw = user?.userGroup;
    if (!raw) return null;
    if (typeof raw === 'object' && raw._id) return raw._id.toString();
    return raw.toString();
  }, [user]);

  const defaultGroupId = useMemo(() => {
    const raw = user?.selectedUserGroup;
    if (!raw) return null;
    if (typeof raw === 'object' && raw._id) return raw._id.toString();
    return raw.toString();
  }, [user]);

  // The active group's label lives on user.groups[].userGroup.label — the
  // top-level user.userGroup is often just an ObjectId reference.
  const activeGroupEntry = useMemo(
    () => groups.find((g) => g.userGroup?._id?.toString() === currentGroupId),
    [groups, currentGroupId]
  );

  if (!activeGroupEntry) return avatarSlot || null;

  // The chip's "User Group" shape combines the populated UserGroup doc with
  // the user's per-group userType and the active-group favicon resolved by
  // ThemeContext (set after auth — see Dashboard.jsx).
  const chipUserGroup = {
    _id: activeGroupEntry.userGroup?._id,
    label: activeGroupEntry.userGroup?.label,
    userType: activeGroupEntry.userType,
    faviconUrl: faviconUrl || null
  };

  const roleFullName =
    CONSTANTS.userTypeOptionsMap?.[activeGroupEntry.userType] ||
    activeGroupEntry.userType ||
    '';

  const handleOpen = (e) => {
    if (!isMultiGroup) return;
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleSwitch = async (userGroupId, isCurrent) => {
    if (isCurrent) {
      handleClose();
      return;
    }
    handleClose();
    try {
      await axios.post(APICONSTANTS.switchUserGroup, { userGroupId });
      // beforeunload handlers in editor pages (e.g. PedigreeSideBar) will
      // surface the native browser prompt if the user has unsaved changes.
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch User Group', err);
    }
  };

  const handleSetDefault = async (userGroupId) => {
    try {
      await axios.post(APICONSTANTS.switchUserGroup, {
        userGroupId,
        setAsDefault: true
      });
      window.location.reload();
    } catch (err) {
      console.error('Failed to set default User Group', err);
    }
  };

  const handleClearDefault = async () => {
    handleClose();
    try {
      await axios.post(APICONSTANTS.clearDefaultUserGroup);
      window.location.reload();
    } catch (err) {
      console.error('Failed to clear default User Group', err);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <UserGroupChip
        userGroup={chipUserGroup}
        roleFullName={roleFullName}
        isMultiGroup={isMultiGroup}
        isMenuOpen={Boolean(anchorEl)}
        onClick={handleOpen}
      />
      {/* The avatar overlaps the chip's right edge. z-index + a box-shadow
          matching the navbar colour masks the chip's square right corner
          behind the avatar's circle. */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          borderRadius: '50%',
          boxShadow: `0 0 0 1.5px ${navbarColor || 'transparent'}`,
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {avatarSlot}
      </Box>
      {isMultiGroup && (
        <UserGroupSwitcherMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          groups={groups}
          currentGroupId={currentGroupId}
          defaultGroupId={defaultGroupId}
          accentColor={accentColor}
          onSwitch={handleSwitch}
          onSetDefault={handleSetDefault}
          onClearDefault={handleClearDefault}
        />
      )}
    </Box>
  );
};

export default UserGroupChipCluster;
